import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AxiosError } from "axios";
import { Modal } from "antd";

import { CollectResponse, EItemComparisonType, EItemType, IItem, IItemOption, IStoredChat, Response } from "./interfaces";
import { ISimuladorResponse } from "../RespostasSimulador/interfaces";

import ResponseService from "../../services/http/simulador/ResponseService";
import { InputSendResponse } from "./components/Input";
import { convertDataUSParaBrasilEHora } from "../../services/Conversores";
import FluxoService from "../../services/http/simulador/FluxoService";

export default function useSimuladorInteligente() {
  const [items, setItems] = useState<IItem[]>([]);
  const [history, setHistory] = useState<IItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isWritingMessage, setIsWritingMessage] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const storedResponseDataRef = useRef<ISimuladorResponse | null>(null);

  const currentItem = useMemo(() => {
    if (!history.length) {
      return null;
    }

    return history[history.length - 1];
  }, [history]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }, 500);
  }, []);

  const updateStoredResponse = useCallback((history: IItem[]) => {
    const storedResponse = localStorage.getItem('@DesenvolveSP:notFinishedResponse');

    if (!storedResponse) {
      return;
    }

    const response: IStoredChat = JSON.parse(storedResponse);

    const updatedResponse = {
      ...response,
      history: history,
    }

    localStorage.setItem('@DesenvolveSP:notFinishedResponse', JSON.stringify(updatedResponse));
  }, []);

  const pushResponse = useCallback((item: IItem, response: Response | CollectResponse[] | string | number | IItemOption[]) => {
    setHistory((prevState) => {

      const responseItem = {
        ...item,
        response,
      }

      const newState = [...prevState, responseItem];

      updateStoredResponse(newState);

      return newState;
    });

    scrollToBottom();
  }, [scrollToBottom, updateStoredResponse]);

  const startChat = useCallback((items: IItem[]) => {
    const initItem = items.find((e) => e.is_initial_item);

    if (initItem) {
      setHistory([initItem]);
    }

    setIsLoading(false);
  }, []);

  const restoreStoredChat = useCallback((storedChat: IStoredChat) => {
    setHistory(storedChat.history);

    storedResponseDataRef.current = {
      _id: storedChat.id,
      respostas: [],
    }

    setIsLoading(false);
    scrollToBottom();
  }, [scrollToBottom]);

  const pushNextItem = useCallback(({
    lastItem,
    itemsList,
    next,
  }: {
    lastItem: IItem,
    itemsList: IItem[],
    next?: string;
  }) => {
    setIsWritingMessage(true);

    scrollToBottom();

    let nextItem = lastItem?.itens?.find((e) => e.id === (next || lastItem?.next));

    const skipWritingFinishing = nextItem && [EItemType.COMPARISON].includes(nextItem.type);
    const timeout = skipWritingFinishing ? 0 : 1500;

    const timeoutId = setTimeout(() => {
      setHistory((prevState) => {

        if (!nextItem) {
          nextItem = itemsList.find((e) => e.id === (next || lastItem?.next));
        }

        if (nextItem) {
          const newState = [...prevState, nextItem];

          updateStoredResponse(newState);

          return newState;
        }

        return prevState;
      })

      if (!skipWritingFinishing) {
        setIsWritingMessage(false);
      }

      scrollToBottom();
    }, timeout);

    return timeoutId;
  }, [scrollToBottom, updateStoredResponse]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadItems() {
      try {
        setIsLoading(true);
        const items = await FluxoService.getItems(controller.signal);

        setItems(items);

        const previousResponse = localStorage.getItem('@DesenvolveSP:notFinishedResponse');

        if (previousResponse) {
          const response: IStoredChat = JSON.parse(previousResponse);

          Modal.info({
            title: `Existe uma resposta não finalizada neste dispositivo, iniciada em ${convertDataUSParaBrasilEHora(response.startedAt)}.`,
            content: 'Deseja continuar de onde parou?',
            cancelText: "Não, recomeçar",
            okText: "Sim",
            okCancel: true,
            onCancel: () => {
              startChat(items);
              localStorage.removeItem('@DesenvolveSP:notFinishedResponse');

              const answeredSomeItem = !!response.history.find((e) => [EItemType.OPTIONS, EItemType.FIELD].includes(e.type) && e.response);

              if (!answeredSomeItem) {
                ResponseService.deleteResponse(response.id);
              }
            },
            onOk: () => restoreStoredChat(response),
            closable: false,
            okButtonProps: {
              style: {
                backgroundColor: '#2D66A2',
                borderColor: '#2D66A2'
              }
            },
            style: {
              fontFamily: 'DM Sans'
            }
          })

          return;
        }

        startChat(items);
      } catch (err) {
        if (err instanceof AxiosError && err.name === "CanceledError") {
          return;
        }

        setIsLoading(false);
      }
    }

    loadItems();

    return () => {
      controller.abort();
    }
  }, [startChat, restoreStoredChat]);

  const finishResponse = useCallback(async () => {
    if (!storedResponseDataRef.current) {
      return;
    }

    await ResponseService.finishResponse(storedResponseDataRef.current._id);
    localStorage.removeItem('@DesenvolveSP:notFinishedResponse');
  }, []);

  useEffect(() => {
    if (!history.length) {
      return;
    }

    const lastItem = history[history.length - 1];

    let timeoutId: NodeJS.Timeout | undefined = undefined;

    if (lastItem.type === EItemType.RESULT && storedResponseDataRef.current) {
      ResponseService.updateResponse(storedResponseDataRef.current._id, {
        id: lastItem.id,
        type: lastItem.type,
        result: lastItem.line_of_credit,
        table_display_name: lastItem.table_display_name,
      });
    }

    if (lastItem.type === EItemType.COMPARISON) {
      const values: {
        [key: string]: string | number | string[];
      } = {};

      lastItem.values_from?.forEach((field) => {
        const response = history.find((e) => e.id === field && e.response)?.response;

        if (typeof response !== "string" && typeof response !== "number") {
          if (response && 'text' in response) {
            values[field] = response.text || "";

            return;
          }

          if (Array.isArray(response)) {
            values[field] = (response as IItemOption[]).map((e) => e.text);
          }

          return;
        }

        values[field] = response;
      });

      const next = lastItem.comparisons?.find((condition) => {
        const satisfies = condition.conditions.every((c) => {
          const value = values[c.field];

          if (c.type === EItemComparisonType.VALUES) {
            if (!Array.isArray(value)) {
              return c.values?.includes(value);
            }

            return value.some((v) => c.values?.includes(v));
          }

          if (c.type === EItemComparisonType.NUMBER_INTERVAL && c.min_value !== undefined && c.max_value !== undefined) {
            const numberValue = Number(value);

            return numberValue >= c.min_value && numberValue <= c.max_value;
          }

          return false;
        })

        return satisfies;
      })?.next || lastItem.default;

      if (!next) {
        console.log('Error: COMPARISON - No condition satisfies');
        return;
      }

      pushNextItem({ lastItem, itemsList: items, next });
    }

    if (lastItem.next === null || (lastItem.type === EItemType.RESULT && lastItem.next === "")) {
      finishResponse();
      return;
    }

    if ([EItemType.MESSAGE, EItemType.RESULT].includes(lastItem.type)) {
      timeoutId = pushNextItem({ lastItem, itemsList: items });
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }, [history, items, pushNextItem, finishResponse]);

  const handleSend = useCallback(async ({
    item,
    collectResponse,
    optionResponse,
    singleResponse,
  }: InputSendResponse) => {
    const getResponseData = () => {
      switch (item.type) {
        case EItemType.COLLECT:
          return collectResponse ?? null;
        case EItemType.OPTIONS:
          return optionResponse?.map((option) => option.text) ?? null;
        case EItemType.FIELD:
          return singleResponse ?? null;
        default:
          return null;
      }
    };

    const response = getResponseData();

    if (!response) return false;

    const basePayload = {
      id: item.id,
      type: item.type,
      message_text: item.message_text,
      response,
      table_display_name: item.table_display_name,
      ...(item.type === EItemType.FIELD && item.format ? { format: item.format } : {}),
    };

    if (!storedResponseDataRef.current) {
      try {
        const data = await ResponseService.createResponse(basePayload);
        storedResponseDataRef.current = data;
        localStorage.setItem('@DesenvolveSP:notFinishedResponse', JSON.stringify({ id: data._id, startedAt: new Date().toISOString() }));
      } catch (error) {
        return false;
      }
    } else {
      try {
        await ResponseService.updateResponse(storedResponseDataRef.current._id, basePayload);
      } catch (error) {
        return false;
      }
    }

    switch (item.type) {
      case EItemType.COLLECT:
        pushResponse(item, collectResponse!);
        pushNextItem({ lastItem: item, itemsList: items });
        break;
      case EItemType.OPTIONS:
        pushResponse(item, optionResponse!);
        pushNextItem({ lastItem: item, next: item.allow_multiple_options ? undefined : optionResponse?.[0]!.next, itemsList: items });
        break;
      case EItemType.FIELD:
        pushResponse(item, singleResponse!);
        pushNextItem({ lastItem: item, itemsList: items });
        break;
    }

    return true;
  }, [pushResponse, pushNextItem, items]);

  const handleReset = useCallback(async () => {
    try {
      const answeredSomeItem = !!history.find((e) => [EItemType.OPTIONS, EItemType.FIELD].includes(e.type) && e.response);

      if (!answeredSomeItem && storedResponseDataRef.current) {
        await ResponseService.deleteResponse(storedResponseDataRef.current._id);
      }

      setHistory([]);
      storedResponseDataRef.current = null;
      startChat(items);
      localStorage.removeItem('@DesenvolveSP:notFinishedResponse');
    } catch (error) {

    }
  }, [items, history]);

  return {
    history,
    isLoading,
    isWritingMessage,
    messagesEndRef,
    currentItem,
    handleSend,
    storedResponseDataRef,
    handleReset,
  }
}
